import React from 'react';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import { ButtonBase } from '@material-ui/core';
import ChatMark from './ChatMark';
import { openKakaoChannel, callPhone, PHONE_NUMBER } from './chatLinks';
import tokens from '../theme/tokens';

// 실시간문의 홈 — 인사 카드 + [새 문의하기] + 나의 문의 목록 + 다른 방법으로 문의(카카오톡·전화)
const useStyles = makeStyles(() => ({
  root: {
    flex: '1 1 auto',
    minHeight: 0,
    overflowY: 'auto',
    padding: '16px 16px 20px',
    backgroundColor: '#fffaf8',
    WebkitOverflowScrolling: 'touch',
  },
  card: {
    padding: '18px 18px 16px',
    borderRadius: 16,
    backgroundColor: tokens.surface,
    boxShadow: '0 4px 14px rgba(22, 22, 23, 0.06)',
  },
  greet: {
    display: 'flex',
    alignItems: 'flex-start',
  },
  greetMark: { marginRight: 12, flex: 'none' },
  greetTitle: {
    fontSize: 16,
    fontWeight: 700,
    letterSpacing: -0.8,
    color: tokens.ink,
    lineHeight: 1.3,
  },
  greetLine: {
    marginTop: 6,
    fontSize: 13,
    lineHeight: 1.6,
    letterSpacing: -0.4,
    color: tokens.text,
    wordBreak: 'keep-all',
  },
  newBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: 48,
    marginTop: 16,
    borderRadius: 24,
    backgroundImage: 'linear-gradient(to right, #ff925c 0%, #f77c40 100%)',
    color: tokens.surface,
    fontSize: 15,
    fontWeight: 700,
    letterSpacing: -0.6,
    transition: 'filter .12s',
    '&:hover': { filter: 'brightness(0.97)' },
    '& svg': { width: 16, height: 16, marginRight: 6 },
  },
  section: {
    marginTop: 18,
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
    padding: '0 4px',
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: -0.4,
    color: tokens.muted,
  },
  list: {
    borderRadius: 16,
    backgroundColor: tokens.surface,
    boxShadow: '0 4px 14px rgba(22, 22, 23, 0.06)',
    overflow: 'hidden',
  },
  room: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    padding: '12px 14px',
    textAlign: 'left',
    borderBottom: `1px solid ${tokens.lineSoft}`,
    transition: 'background-color .12s',
    '&:last-child': { borderBottom: 'none' },
    '&:hover': { backgroundColor: tokens.primarySoft },
  },
  roomMain: { flex: '1 1 auto', minWidth: 0 },
  roomTop: {
    display: 'flex',
    alignItems: 'center',
    '& b': { flex: '1 1 auto', minWidth: 0, fontSize: 14, color: tokens.ink, letterSpacing: -0.6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    '& em': { flex: 'none', marginLeft: 8, fontStyle: 'normal', fontSize: 11, color: tokens.muted },
  },
  roomBot: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 3,
    '& small': { flex: '1 1 auto', minWidth: 0, fontSize: 12, color: tokens.text, letterSpacing: -0.4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  },
  ended: { '& small': { color: tokens.muted } },
  unread: {
    flex: 'none',
    minWidth: 20,
    height: 20,
    marginLeft: 8,
    padding: '0 6px',
    borderRadius: 10,
    backgroundColor: tokens.primaryDark,
    color: tokens.surface,
    fontSize: 11,
    fontWeight: 700,
    lineHeight: '20px',
    textAlign: 'center',
  },
  state: {
    flex: 'none',
    marginLeft: 8,
    fontSize: 11,
    color: tokens.muted,
  },
  others: {
    display: 'flex',
    gap: 8,
  },
  other: {
    flex: '1 1 0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 44,
    borderRadius: 12,
    border: `1px solid ${tokens.line}`,
    backgroundColor: tokens.surface,
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: -0.4,
    color: tokens.ink,
    '&:hover': { borderColor: tokens.primary, color: tokens.primaryDark },
    '& svg': { width: 18, height: 18, marginRight: 7 },
  },
}));

const fmtTime = (at) => {
  const d = new Date(at);
  if (isNaN(d.getTime())) return '';
  const now = new Date();
  const same = d.toDateString() === now.toDateString();
  const hm = `${d.getHours() < 12 ? '오전' : '오후'} ${d.getHours() % 12 || 12}:${String(d.getMinutes()).padStart(2, '0')}`;
  return same ? hm : `${d.getMonth() + 1}/${d.getDate()}`;
};

function ChatHome({ rooms, onNew, onOpen }) {
  const cs = useStyles();
  return (
    <div className={cs.root}>
      <div className={cs.card}>
        <div className={cs.greet}>
          <ChatMark className={cs.greetMark} size={44} ring />
          <div>
            <div className={cs.greetTitle}>안녕하세요, 퀴노스입니다.</div>
            <div className={cs.greetLine}>문의를 남겨 주시면 담당자가 확인 후 바로 답변드립니다.</div>
          </div>
        </div>
        <ButtonBase className={cs.newBtn} onClick={onNew} focusRipple={false}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
          새 문의하기
        </ButtonBase>
      </div>

      {rooms.length > 0 && (
        <div className={cs.section}>
          <div className={cs.sectionTitle}><span>나의 문의</span><span>{rooms.length}</span></div>
          <div className={cs.list}>
            {rooms.map((r) => (
              <ButtonBase key={r.id} className={clsx(cs.room, r.status === 'ended' && cs.ended)} onClick={() => onOpen(r.id)} focusRipple={false}>
                <div className={cs.roomMain}>
                  <div className={cs.roomTop}>
                    <b>{r.title || `문의 ${r.seq}`}</b>
                    <em>{fmtTime(r.lastMessageAt)}</em>
                  </div>
                  <div className={cs.roomBot}>
                    <small>{r.lastMessage || '대화를 시작해 보세요'}</small>
                    {r.unread > 0 ? <span className={cs.unread}>{r.unread > 99 ? '99+' : r.unread}</span> : (r.status === 'ended' && <span className={cs.state}>상담 종료</span>)}
                  </div>
                </div>
              </ButtonBase>
            ))}
          </div>
        </div>
      )}

      <div className={cs.section}>
        <div className={cs.sectionTitle}><span>다른 방법으로 문의</span></div>
        <div className={cs.others}>
          <ButtonBase className={cs.other} onClick={openKakaoChannel} focusRipple={false}>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path fill="#391b1b" d="M12 3C6.5 3 2 6.5 2 10.8c0 2.8 1.9 5.2 4.7 6.6l-1 3.7c-.1.3.3.6.6.4l4.4-2.9c.4 0 .9.1 1.3.1 5.5 0 10-3.5 10-7.9S17.5 3 12 3z" /></svg>
            카카오톡 문의
          </ButtonBase>
          <ButtonBase className={cs.other} onClick={callPhone} focusRipple={false} title={PHONE_NUMBER}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2z" /></svg>
            전화 문의
          </ButtonBase>
        </div>
      </div>
    </div>
  );
}

export default ChatHome;
